/**
 * NS typography tokens as TypeScript constants for MUI theme consumption.
 *
 * SSOT model:
 * - `tokens.css` is canonical at runtime — Backstage applies its custom
 *   properties via `:root` / `[data-theme-mode='dark']`.
 * - This file mirrors `tokens.css` cluster 7 (`--ns-font-size-*`,
 *   `--ns-space-*`, `--ns-font-family-regular`, `--ns-font-weight-medium`)
 *   for type-safe TypeScript consumption inside `theme.ts`.
 * - Drift between the two is detected at test time by the anti-duplicatie
 *   suite in `theme.test.ts` (parses `tokens.css` and asserts every constant
 *   here matches its canonical custom-property value).
 *
 * Mode-invariant: typography is identical in light + dark per
 * `TOKEN-ARCHITECTURE.md`.
 */

/* Heading font-sizes (TOKENS.md "Cluster 7: typography") */

/** `--ns-font-size-xxxl` — fluid 22-28px (cap 28). MUI `typography.h1.fontSize`. */
export const H1_FONT_SIZE = 'clamp(1.375rem, 1.125rem + 0.8333vw, 1.75rem)';

/** `--ns-font-size-xxl` — fluid 20-24px (cap 24). MUI `typography.h2.fontSize`. */
export const H2_FONT_SIZE = 'clamp(1.25rem, 1.0833rem + 0.5556vw, 1.5rem)';

/** `--ns-font-size-xl` — fluid 18-20px (cap 20). MUI `typography.h3.fontSize`. */
export const H3_FONT_SIZE = 'clamp(1.125rem, 1.0417rem + 0.2778vw, 1.25rem)';

/** `--ns-font-size-l` — fluid 16-18px (cap 18). MUI `typography.h4.fontSize`. */
export const H4_FONT_SIZE = 'clamp(1rem, 0.9167rem + 0.2778vw, 1.125rem)';

/** `--ns-font-size-m` — 16px fixed. MUI `typography.h5.fontSize`. */
export const H5_FONT_SIZE = '1rem';

/** `--ns-font-size-s` — 14px fixed. MUI `typography.h6.fontSize`. */
export const H6_FONT_SIZE = '0.875rem';

/* Heading font-weight (Tractie-medium per designer-default 7.11 herzien) */

/** `--ns-font-weight-medium` — 500. Heading weight (NIET 700, NIET MUI default). */
export const HEADING_FONT_WEIGHT = 500;

/* Heading margin-bottom (TOKENS.md "Cluster 7: typography", spacer-ladder mapping) */

/** h1: 8px (`--ns-space-2`, 2px afronding van Backstage-default 10). */
export const H1_MARGIN_BOTTOM = 8;

/** h2: 8px (`--ns-space-2`). */
export const H2_MARGIN_BOTTOM = 8;

/** h3: 6px (`--ns-space-1-5`). */
export const H3_MARGIN_BOTTOM = 6;

/** h4: 6px (`--ns-space-1-5`). */
export const H4_MARGIN_BOTTOM = 6;

/** h5: 4px (`--ns-space-1`). */
export const H5_MARGIN_BOTTOM = 4;

/** h6: 2px (`--ns-space-0-5`). */
export const H6_MARGIN_BOTTOM = 2;

/* Body / button typography */

/** roboto stack — `--ns-font-family-regular`. */
export const FONT_FAMILY = 'roboto, sans-serif';

/** MUI `typography.htmlFontSize`. Constant 16 = root-rem convention. */
export const HTML_FONT_SIZE = 16;

/** MUI `typography.button.fontWeight` — bold (700). */
export const BUTTON_FONT_WEIGHT = 700;
